import { useCallback, useEffect, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { z } from "zod";

import {
	POSConnectorClient,
	WS_CLOSE_EVENT_CODE_1000_NORMAL_CLOSURE,
} from "../../../@lib-pos-connector-client";
import { logger } from "../../../shared/logger";
import {
	createMessageGuard,
	createReconnector,
	STATE_INITIAL_TIMEOUT,
} from "../shared/pos-connector-session";
import type { Route } from "../shared/route";
import { useHealthcheck } from "../shared/use-healthcheck";

export type HopStatus = "checking" | "connected" | "disconnected";

/**
 * Each hop between this device and its paired terminal: `internet` is the SDK
 * API, `cloud` the POS connector socket, `terminal` the paired terminal's own
 * connection to the POS connector.
 */
export type ConnectionStatus = {
	internet: HopStatus;
	cloud: HopStatus;
	terminal: HopStatus;
};

export type ConnectionCheck = ConnectionStatus & { check: () => void };

// API Gateway drops a websocket after 10 minutes without traffic.
export const KEEP_ALIVE_INTERVAL = 5 * 60 * 1000;

const CHECKING: ConnectionStatus = {
	internet: "checking",
	cloud: "checking",
	terminal: "checking",
};

const GetPairedDevicesResponseSchema = z.object({
	action: z.literal("getPairedDevices"),
	data: z.object({
		connectedDevices: z.array(
			z.object({ deviceUuid: z.string(), status: z.string() }),
		),
	}),
});

const PairedDeviceUpdateSchema = z.object({
	action: z.literal("pairedDeviceUpdate"),
	data: z.object({
		deviceUuid: z.string(),
		connectionStatus: z.string(),
	}),
});

/**
 * Whether the paired `terminalId` is connected to the POS connector, asked
 * over a socket opened as `deviceId`. Checks each hop in order: the SDK API,
 * then the socket, then `getPairedDevices`; a failed hop stops the run. Then
 * follows `pairedDeviceUpdate`, and asks again on every reconnect. Anything
 * that leaves a hop unknown reads as disconnected. `check` reruns the checks,
 * reusing the socket while it is open.
 */
export function useConnectionStatus({
	deviceId,
	terminalId,
	tokens,
}: {
	deviceId: string | undefined;
	terminalId: string | undefined;
	tokens: Route.TokensResolveFn;
}): ConnectionCheck {
	const healthcheck = useHealthcheck();
	const [status, setStatus] = useState<ConnectionStatus>(CHECKING);
	const checkRef = useRef<() => void>(() => undefined);

	useEffect(() => {
		setStatus(CHECKING);
		if (!deviceId || !terminalId) {
			return;
		}
		const id = deviceId;
		const terminal = terminalId;

		let disposed = false;
		let _timeout: ReturnType<typeof setTimeout> | undefined;
		let _run = 0;
		let _connection: POSConnectorClient | undefined;
		const keepAlive = setInterval(() => {
			_connection?.ping();
		}, KEEP_ALIVE_INTERVAL);

		function updateTimeout(callback: () => void, delay: number) {
			clearTimeout(_timeout);
			_timeout = setTimeout(callback, delay);
		}

		function update(next: Partial<ConnectionStatus>) {
			if (disposed) return;
			setStatus((status) => ({ ...status, ...next }));
		}

		function disconnected(cause: string, next: Partial<ConnectionStatus>) {
			logger.warn("setup: connection check failed", { cause });
			update(next);
		}

		async function checkInternet() {
			const current = _run;
			const result = await healthcheck(id);
			// A newer check supersedes this one.
			if (current !== _run) return;
			update({
				internet: result instanceof Error ? "disconnected" : "connected",
			});
		}

		function requestPairedDevices(connection: POSConnectorClient) {
			const current = _run;
			updateTimeout(() => {
				if (current !== _run) return;
				disconnected("no reply", { terminal: "disconnected" });
			}, STATE_INITIAL_TIMEOUT);
			connection.send({
				action: "getPairedDevices",
				eventId: uuidv4(),
				timestamp: Date.now().toString(),
				data: null,
			});
		}

		const reconnector = createReconnector({
			deviceId: id,
			healthcheck,
			updateTimeout,
			connect: () => void connect(),
			onExhausted: () => {
				disconnected("reconnects exhausted", {
					cloud: "disconnected",
					terminal: "disconnected",
				});
				void checkInternet();
			},
			onNetworkError: () =>
				disconnected("network", {
					internet: "disconnected",
					cloud: "disconnected",
					terminal: "disconnected",
				}),
		});

		const handle = createMessageGuard({
			onUnreachable: () =>
				disconnected("unreachable", { terminal: "disconnected" }),
			onForbidden: () =>
				disconnected("forbidden", { terminal: "disconnected" }),
			onOperationError: () =>
				disconnected("operation", { terminal: "disconnected" }),
		});

		async function run() {
			const current = ++_run;
			clearTimeout(_timeout);
			update(CHECKING);

			const result = await healthcheck(id);
			// A newer check, or unmount, supersedes this one.
			if (disposed || current !== _run) return;
			if (result instanceof Error) {
				return disconnected("network", {
					internet: "disconnected",
					cloud: "disconnected",
					terminal: "disconnected",
				});
			}
			update({ internet: "connected" });

			if (_connection?.ready) {
				update({ cloud: "connected" });
				return requestPairedDevices(_connection);
			}
			reconnector.reset();
			void connect();
		}

		checkRef.current = () => void run();
		void run();

		async function connect() {
			const current = _run;
			// Covers a token or socket that never settles; open swaps it for the reply timeout.
			updateTimeout(() => {
				if (current !== _run) return;
				disconnected("no open", {
					cloud: "disconnected",
					terminal: "disconnected",
				});
			}, STATE_INITIAL_TIMEOUT);
			const authToken = (await tokens())?.authAccessToken;
			if (disposed || current !== _run) return;
			if (!authToken) {
				return disconnected("unauthenticated", {
					cloud: "disconnected",
					terminal: "disconnected",
				});
			}

			_connection?.close();
			const connection = POSConnectorClient({
				authToken,
				deviceUuid: id,
				// Our own close clears this handler, so a normal close here is the server's.
				onClose: (event) =>
					event.code === WS_CLOSE_EVENT_CODE_1000_NORMAL_CLOSURE
						? reconnector.reconnect()
						: reconnector.onClose(event),
				onError: reconnector.onError,
			});
			_connection = connection;

			connection.onOpen(() => {
				update({ internet: "connected", cloud: "connected" });
				requestPairedDevices(connection);
			});

			connection.onMessage(
				handle((message) => {
					reconnector.reset();

					const list = GetPairedDevicesResponseSchema.safeParse(message).data;
					if (list) {
						clearTimeout(_timeout);
						const connected = list.data.connectedDevices.some(
							(device) =>
								device.deviceUuid === terminal && device.status === "CONNECTED",
						);
						return connected
							? update({ terminal: "connected" })
							: disconnected("not connected", { terminal: "disconnected" });
					}

					const event = PairedDeviceUpdateSchema.safeParse(message).data;
					if (event?.data.deviceUuid === terminal) {
						clearTimeout(_timeout);
						return event.data.connectionStatus === "CONNECTED"
							? update({ terminal: "connected" })
							: disconnected("disconnected update", {
									terminal: "disconnected",
								});
					}
				}),
			);
		}

		return () => {
			disposed = true;
			checkRef.current = () => undefined;
			clearTimeout(_timeout);
			clearInterval(keepAlive);
			_connection?.close();
		};
	}, [deviceId, terminalId, tokens, healthcheck]);

	const check = useCallback(() => checkRef.current(), []);
	return { ...status, check };
}
